import { v4 } from 'uuid';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation } from '@apollo/client';
import { createLogger } from '../utils/logging';
import { createOnErrorHandler } from '../components/errors/Errors';
import { addCorrelation, removeCorrelation } from '../actions/correlations';
import { showInfoNotification } from '../actions/notifications';
import {
    isExtraBackendConfirmationNeeded,
    isFourEyesPrincipleNeeded,
} from '../components/errors/MiscErrors';
import useIsMounted from './useIsMounted';

const logger = createLogger('useExtendedMutation');

function useExtendedMutation(mutation, { // NOSONAR javascript:S3776
    variables = {},
    successCallback,
    successMessageOptions,
    successMessageCreator,
    failureMessageOptions,
    failureMessageCreator,
    refetchQueries,
    withCorrelationId = true,
    statesToResetOnSuccess = [],
}) {
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    const apolloClient = useApolloClient();
    const [isFourEyesNeeded, setFourEyesNeeded] = useState(false);
    const [isExtraConfirmationNeeded, setExtraConfirmationNeeded] = useState(false);
    const [extraVariables, setExtraVariables] = useState({});
    const dispatch = useDispatch();
    const [correlationId, setCorrelationId] = useState();
    const onCompleted = (data) => {
        logger.debug(`Processing completed mutation: ${successMessageOptions ? successMessageOptions.dataProperty : 'without dataProperty'}`);
        if (successMessageOptions) {
            dispatch(showInfoNotification(t(successMessageOptions.key, data[successMessageOptions.dataProperty])));
        } else if (successMessageCreator) {
            dispatch(showInfoNotification(successMessageCreator(data)));
        }
        successCallback && successCallback(data);
        if (isMounted()) {
            setFourEyesNeeded(false);
        }
        statesToResetOnSuccess.forEach((stateToReset) => {
            stateToReset.changeInitialState(stateToReset.value);
            stateToReset.reset();
        });
    };
    const onError = createOnErrorHandler(dispatch, apolloClient, failureMessageOptions ? t(failureMessageOptions.key, failureMessageOptions.params) : failureMessageCreator());
    const [mutationCallback, { loading }] = useMutation(mutation, {
        onError: (error) => {
            if (isFourEyesPrincipleNeeded(error) || isExtraBackendConfirmationNeeded(error)) {
                if (isFourEyesPrincipleNeeded(error)) {
                    setFourEyesNeeded(true);
                }
                if (isExtraBackendConfirmationNeeded(error)) {
                    setExtraConfirmationNeeded(true);
                }
            } else {
                logger.error(error);
            }
            onError(error);
        },
        onCompleted,
        refetchQueries,
        awaitRefetchQueries: true,
    });
    const mutate = async (newExtraVariables) => {
        const wasFourEyesNeeded = isFourEyesNeeded;
        const wasExtraConfirmationNeeded = isExtraConfirmationNeeded;
        setExtraConfirmationNeeded(false);
        setExtraVariables(newExtraVariables);
        let updatedVariables = { ...variables };
        if (withCorrelationId) {
            const newCorrelationId = v4();
            setCorrelationId(newCorrelationId);
            dispatch(addCorrelation(newCorrelationId));
            updatedVariables.correlationId = newCorrelationId;
        }
        updatedVariables = { ...updatedVariables, ...newExtraVariables };
        if (wasFourEyesNeeded || wasExtraConfirmationNeeded) {
            dispatch(removeCorrelation(correlationId));
        }
        logger.debug(`Mutation called: ${mutation.definitions.map((definition) => definition.name.value).join()}; ${JSON.stringify(updatedVariables)}`);
        return mutationCallback({
            variables: Object.getOwnPropertyNames(updatedVariables).length > 0 ? updatedVariables : null,
        });
    };
    const reset = () => {
        setFourEyesNeeded(false);
        setExtraConfirmationNeeded(false);
        setExtraVariables({});
    };
    return {
        mutate,
        reset,
        isFourEyesNeeded,
        isExtraConfirmationNeeded,
        extraVariables,
        loading,
    };
}

export default useExtendedMutation;
